import { FC, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Components
import { Switcher } from '@geneui/components';
import CJTemplateId from '../CJTemplateId';
import CJProviderId from '../CJProviderId';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useDNDCustomRowData } from 'components/DraggableForm';
// Import Types
import { DeliveryMethodConfig, DeliveryMethodData, DependencyObject, CJDynamicVariables } from './types';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
// Import Styles
import styles from './CJBestMessageSender.module.scss';

interface CJBestMessageDeliveryMethodRowProps {
    config: DeliveryMethodConfig;
    data: DeliveryMethodData;
    onChange: (data: DeliveryMethodData) => void;
    getDynamicVariablesUpdate: (value: CJDynamicVariables) => void;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    updateDependency: (data: DependencyObject) => void;
    parentKey: string;
    index: number;
}

const CJBestMessageDeliveryMethodRow: FC<CJBestMessageDeliveryMethodRowProps> = ({
    config,
    data,
    onChange,
    getDynamicVariablesUpdate,
    setIsLoading,
    updateDependency,
    parentKey,
    index,
}) => {
    const { doGetRequest } = useRequest();
    const { t } = useTranslation();
    useDNDCustomRowData({
        parentName: parentKey,
        index,
    });

    const { getTemplateModels } = useCancelableRequests(
        { getTemplateModels: CustomerJourneyHttpService.getTemplateModels },
        { takeLatest: ['getTemplateModels'] },
    );

    const onToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked === false) {
            onChange({ ...data, isEnabled: event.target.checked, dynamicVariables: {} });
        }
        onChange({ ...data, isEnabled: event.target.checked });
    };

    const templateChangeHandler = (template: { value: number } | null) => {
        onChange({ ...data, template });
    };

    const providerChangeHandler = (provider: { value: number } | null) => {
        onChange({ ...data, provider });
    };

    useEffect(() => {
        if (!isNil(data?.template) && !isNil(data?.template?.value)) {
            const templateId = data.template.value;
            setIsLoading(true);
            doGetRequest(getTemplateModels.request, {
                queryString: { templateId: templateId },
                successCallback: (Data) => {
                    const dependencyObject = {
                        dependencyItemId: templateId,
                        templateId: templateId,
                        dynamicVariables: Data.reduce((acc, { FieldName, DisplayNameKey, WfType }) => {
                            acc[FieldName] = {
                                fieldName: FieldName,
                                displayNameKey: DisplayNameKey,
                                wfType: WfType,
                            };

                            return acc;
                        }, {}),
                    };
                    updateDependency(dependencyObject);

                    const getIsChecked = (dynamicVariables, fieldName) => {
                        return dynamicVariables[fieldName]?.isChecked ?? false;
                    };

                    const dynamicVariablesModel = Data.reduce((acc, item) => {
                        const { FieldName, DisplayNameKey, WfType } = item;

                        acc[FieldName] = {
                            fieldName: FieldName,
                            displayNameKey: DisplayNameKey,
                            wfType: WfType,
                            value: data?.dynamicVariables?.[FieldName]?.value ?? {},
                            isChecked: getIsChecked(data?.dynamicVariables, FieldName),
                        };
                        return acc;
                    }, {});

                    getDynamicVariablesUpdate(dynamicVariablesModel);
                },
            }).then(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.template?.value]);

    return (
        <div className={styles.cjBestChannelRowWrapper}>
            <div className={styles.toggleWrapper}>
                <Switcher checked={data.isEnabled} onChange={onToggle} label={t(config.deliveryMethodName)} />
            </div>
            <div className={styles.dropdownsWrapper}>
                <CJTemplateId
                    defaultValue={data.template}
                    getUpdate={templateChangeHandler}
                    option={{ ...config.templateOption, Required: data.isEnabled }}
                    isDisabled={!data.isEnabled}
                />
                {config?.providerOption && (
                    <CJProviderId
                        defaultValue={data.provider}
                        getUpdate={providerChangeHandler}
                        option={config.providerOption}
                        isDisabled={!data.isEnabled}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(CJBestMessageDeliveryMethodRow);
